import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ScrollingNav } from '../../components/scrolling-nav';
import { VizComponent as Viz } from '../viz';
import Discover from '../../common/redux/actions/DiscoverActions';
import _, { isUndefined } from 'lodash';
import { getIsReadOnlyUser } from '../../common/redux/selectors/AccountSelectors';
import { messages } from '../../i18n';
import { CloseIcon } from '../../icons/icons';
import { IndicatorBubble } from '../../icons/sugar-icon/sugar-icon.util';
import {
  TabStyled,
  CloseDiscovery,
  DiscoveryDirtyIndicator,
} from './discover-tabs.styles';
import { IDiscoveryTracked, IRevision, IViz } from '../interfaces';
import {
  CreateReportButton,
  MainSectionHeader,
} from '../../components/main-section-header';
import { TruncatedTooltip } from '../../components/ui/truncated-tooltip';
import { useParams } from 'react-router-dom';
import { useNavigateProtected } from '../../common/utilities/with-router';
import { useLazyQuery } from '@apollo/client';
import { VizQueries } from '../../common/graphql';
import { OPEN_VIZ_PATTERN_SUFFIX, ROUTER_DIRS } from '../../common';
import { Viz as VizUtil } from '../VizUtil';

// renders full Viz control panel. should rename
export const DiscoverTabs = () => {
  const {
    openDiscoveries,
    openDiscoveriesTabList,
    isUserReadOnly,
    displayDiscovery,
  } = useSelector((state: any) => {
    const firstOpen = _.keys(state.discover.openDiscoveries)[0];
    const isDisplayDiscovery = _.get(
      state,
      'discover.displayDiscovery',
      firstOpen,
    );
    return {
      openDiscoveries: state.discover.openDiscoveries,
      openDiscoveriesTabList: state.discover.openDiscoveriesTabList,
      displayDiscovery: isDisplayDiscovery || firstOpen,
      isUserReadOnly: getIsReadOnlyUser(state),
    };
  });
  const { [OPEN_VIZ_PATTERN_SUFFIX.replace(':', '')]: discoveryIdParam } =
    useParams();

  const { activeDiscoveryId, isOpen } = useMemo(() => {
    const activeDiscoveryId = displayDiscovery ?? discoveryIdParam;
    const isOpen = !_.isNil(_.get(openDiscoveries, activeDiscoveryId));

    return {
      activeDiscoveryId,
      isOpen,
    };
  }, [discoveryIdParam, displayDiscovery, openDiscoveries]);

  const dispatch = useDispatch();
  const navigate = useNavigateProtected();

  const openVisualization = useCallback(
    (viz: IViz) => {
      dispatch(Discover.openDiscoveryTracked({ viz }, 'tab'));
    },
    [dispatch],
  );

  const [getViz, { loading: vizLoading }] = useLazyQuery(
    VizQueries.GetVisualization,
    {
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (!activeDiscoveryId || isOpen || vizLoading) {
      return;
    }

    getViz({
      variables: { id: activeDiscoveryId },
      onCompleted: ({ visualization: dehydratedViz }) => {
        if (dehydratedViz) {
          openVisualization(VizUtil.rehydrateViz(dehydratedViz));
        } else {
          navigate(ROUTER_DIRS.ROOT);
        }
      },
      onError: () => navigate(ROUTER_DIRS.ROOT),
    });
  }, [
    activeDiscoveryId,
    getViz,
    isOpen,
    openVisualization,
    vizLoading,
    navigate,
  ]);

  const setDisplayDiscovery = id => {
    dispatch(Discover.setDisplayDiscovery(id));
  };
  const closeDiscovery = (id, force = false) => {
    dispatch(Discover.closeDiscovery(id, { force }));
  };

  // maintaining order with open discoveries
  const existingDiscoveryIds = _.filter(
    openDiscoveriesTabList,
    discId => !isUndefined(discId) && !!openDiscoveries[discId],
  );

  const discoveryNavItems = _.map(existingDiscoveryIds, activeDiscoveryId => {
    const discovery: IDiscoveryTracked = openDiscoveries[activeDiscoveryId];
    const { dirty, viz } = discovery?.present;
    const { id, name, canUpdate, updatedOn, revisions } = viz ?? {};
    const lastRevision: IRevision = _.head(revisions);
    const isNewViz = VizUtil.isNewViz(id) && _.isEmpty(revisions);
    const hasUnversionedSaves =
      _.isEmpty(lastRevision) || !_.isEqual(updatedOn, lastRevision.updatedOn);
    return (
      <TabStyled
        key={id}
        label={
          <>
            {canUpdate && dirty && (
              <DiscoveryDirtyIndicator
                title={messages.viz.unsavedChangesAndVersion}
              >
                <IndicatorBubble size={8} />
              </DiscoveryDirtyIndicator>
            )}
            {!_.isNil(viz) &&
              !isNewViz &&
              canUpdate &&
              !dirty &&
              hasUnversionedSaves && (
                <DiscoveryDirtyIndicator title={messages.viz.unsavedVersion}>
                  <IndicatorBubble size={8} empty={true} />
                </DiscoveryDirtyIndicator>
              )}
            <TruncatedTooltip
              arrow
              placement='bottom'
              title={name}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -10],
                      },
                    },
                  ],
                },
              }}
            />
            <CloseDiscovery
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                closeDiscovery(id, isUserReadOnly);
              }}
            >
              <CloseIcon size={10} />
            </CloseDiscovery>
          </>
        }
        value={id}
      />
    );
  });

  return (
    <div className='discover-open-panel'>
      <div className='discover-tab-panel'>
        <MainSectionHeader
          headerLabel={messages.nav.reports}
          actionButtons={[
            !isUserReadOnly && <CreateReportButton key={'create-report'} />,
          ]}
        />
        <ScrollingNav
          activeKey={activeDiscoveryId}
          navItems={discoveryNavItems}
          handleSelect={id => setDisplayDiscovery(id)}
        />
        {isOpen && <Viz key={activeDiscoveryId} vizId={activeDiscoveryId} />}
      </div>
    </div>
  );
};
